import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { IndianRupee, Users, GitBranch, TrendingUp, Clock, CheckCircle2, Award, Zap, Trophy, HelpCircle } from "lucide-react";
import SMTour, { SM_TOUR_KEY } from "./SMTour";
const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const fmtD = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const KPICard = ({ label, value, sub, icon: Icon, color = "primary", testId }) => (
  <div className="bg-card border border-border rounded-xl p-5 flex flex-col gap-2" data-testid={testId}>
    <div className="flex items-center justify-between">
      <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{label}</span>
      <span className={`w-8 h-8 rounded-lg bg-${color}/10 flex items-center justify-center`}>
        <Icon className={`w-4 h-4 text-${color}`} />
      </span>
    </div>
    <div className="text-2xl font-bold text-foreground">{value}</div>
    {sub && <div className="text-[11px] text-muted-foreground">{sub}</div>}
  </div>
);

const TIER_STYLES = {
  Starter:  { bg: "bg-zinc-500/10",   text: "text-zinc-500",   bar: "bg-zinc-400" },
  Bronze:   { bg: "bg-amber-700/10",  text: "text-amber-700",  bar: "bg-amber-700" },
  Silver:   { bg: "bg-slate-400/10",  text: "text-slate-500",  bar: "bg-slate-400" },
  Gold:     { bg: "bg-yellow-500/10", text: "text-yellow-600", bar: "bg-yellow-500" },
  Platinum: { bg: "bg-violet-500/10", text: "text-violet-600", bar: "bg-violet-500" },
  Diamond:  { bg: "bg-cyan-500/10",   text: "text-cyan-600",   bar: "bg-cyan-500" },
};
const LEVEL_LABELS = {
  sales_partner: "SP", sales_manager: "SM", zonal_partner: "ZP",
  regional_partner: "RP", national_partner: "NP",
};
const RANK_STYLES = [
  "w-7 h-7 rounded-full bg-yellow-400/20 text-yellow-600 border-yellow-400/30",
  "w-7 h-7 rounded-full bg-slate-400/20 text-slate-500 border-slate-400/30",
  "w-7 h-7 rounded-full bg-amber-700/20 text-amber-700 border-amber-700/30",
];

function LeaderboardWidget() {
  const [data, setData]     = useState(null);
  const [period, setPeriod] = useState("month");
  const [loading, setLoading] = useState(true);

  const load = useCallback((p) => {
    setLoading(true);
    axios.get(`${API}/api/sm/commission/leaderboard?period=${p}&limit=10`, { withCredentials: true })
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(period); }, [period, load]);

  const PERIODS = [
    { key: "month", label: "This Month" },
    { key: "quarter", label: "Quarter" },
    { key: "all", label: "All Time" },
  ];

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden" data-testid="sm-leaderboard">
      <div className="px-4 py-3.5 border-b border-border flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center gap-2">
          <Trophy className="w-4 h-4 text-yellow-500 shrink-0" />
          <span className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground font-medium">Partner Performance Leaderboard</span>
        </div>
        <div className="flex gap-0.5 bg-muted/50 rounded-lg p-0.5 sm:ml-auto w-fit">
          {PERIODS.map(p => (
            <button key={p.key} onClick={() => setPeriod(p.key)}
              data-testid={`leaderboard-period-${p.key}`}
              className={`px-2.5 py-1.5 rounded-md text-[11px] font-medium transition-all ${
                period === p.key
                  ? "bg-background text-foreground shadow-sm border border-border"
                  : "text-muted-foreground hover:text-foreground"
              }`}>{p.label}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="p-6 text-center text-muted-foreground text-sm animate-pulse">Loading leaderboard…</div>
      ) : !data?.leaderboard?.length ? (
        <div className="p-10 text-center text-muted-foreground text-sm">
          No commission data yet for {data?.period_label || "this period"}. Add partners and record commissions.
        </div>
      ) : (
        <div className="divide-y divide-border/50">
          {data.leaderboard.map((p, i) => {
            const tier = TIER_STYLES[p.tier] || TIER_STYLES.Starter;
            const rankStyle = RANK_STYLES[i] || "w-7 h-7 rounded-full bg-muted text-muted-foreground border-border";
            return (
              <div key={p.partner_id} className="px-5 py-3.5 flex items-center gap-4 hover:bg-muted/20 transition-all"
                data-testid={`leaderboard-row-${i+1}`}>
                {/* Rank */}
                <span className={`flex items-center justify-center text-[11px] font-bold border shrink-0 ${rankStyle}`}>
                  {i < 3 ? ["1","2","3"][i] : p.rank}
                </span>

                {/* Name + Level */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-foreground truncate">{p.partner_name}</span>
                    <span className={`text-[9px] uppercase tracking-[0.12em] px-1.5 py-0.5 rounded font-bold ${tier.bg} ${tier.text}`}>
                      {LEVEL_LABELS[p.partner_type] || p.partner_type}
                    </span>
                  </div>
                  {/* Progress bar */}
                  <div className="mt-1.5 flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-border rounded-full overflow-hidden">
                      <div className={`h-full rounded-full transition-all ${tier.bar}`}
                        style={{ width: `${p.progress_pct}%` }} />
                    </div>
                    <span className={`text-[9px] font-medium ${tier.text} shrink-0`}>{p.tier}</span>
                    {p.next_milestone && (
                      <span className="text-[9px] text-muted-foreground shrink-0">
                        → {fmt(p.next_milestone)}
                      </span>
                    )}
                  </div>
                </div>

                {/* Commission earned (period) */}
                <div className="text-right shrink-0">
                  <div className="text-[14px] font-bold text-foreground">{fmtD(p.total_net)}</div>
                  <div className="text-[10px] text-muted-foreground">{p.event_count} event{p.event_count !== 1 ? "s" : ""}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {data?.leaderboard?.length > 0 && (
        <div className="px-5 py-3 border-t border-border/50">
          <a href="/sm/commission-ledger" className="text-[11px] text-primary hover:underline">
            View full commission ledger →
          </a>
        </div>
      )}
    </div>
  );
}

export default function SMDashboard() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTour, setShowTour] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/sm/commission/dashboard-summary`, { withCredentials: true })
      .then(r => setSummary(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));

    // Auto-show tour on first visit
    if (!localStorage.getItem(SM_TOUR_KEY)) {
      setTimeout(() => setShowTour(true), 600);
    }
  }, []);

  if (loading) return (
    <div className="p-8 text-muted-foreground text-sm animate-pulse">Loading dashboard…</div>
  );

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="sm-dashboard">
      {showTour && <SMTour onClose={() => setShowTour(false)} />}

      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-foreground">Partner Network</h1>
          <p className="text-sm text-muted-foreground mt-0.5">LawOS Channel Partner Commission Engine</p>
        </div>
        <button onClick={() => { localStorage.removeItem(SM_TOUR_KEY); setShowTour(true); }}
          data-testid="sm-start-tour-btn"
          title="Take a guided tour"
          className="flex items-center gap-1.5 px-3 py-2 text-[12px] border border-border text-muted-foreground rounded-lg hover:text-primary hover:border-primary/40 hover:bg-primary/5 transition-all shrink-0">
          <HelpCircle className="w-3.5 h-3.5" />Tour
        </button>
      </div>

      {/* KPI Row 1 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        <KPICard testId="kpi-pending" label="Pending Commissions" icon={Clock}
          value={fmt(summary?.pending_commission?.gross)}
          sub={`${summary?.pending_commission?.count || 0} entries · Net ${fmt(summary?.pending_commission?.net)}`}
          color="amber-500" />
        <KPICard testId="kpi-approved" label="Approved (Unpaid)" icon={CheckCircle2}
          value={fmt(summary?.approved_commission?.gross)}
          sub={`${summary?.approved_commission?.count || 0} entries`}
          color="emerald-500" />
        <KPICard testId="kpi-paid" label="Total Paid Out" icon={IndianRupee}
          value={fmt(summary?.paid_commission?.gross)}
          sub={`Net paid: ${fmt(summary?.paid_commission?.net)}`} />
        <KPICard testId="kpi-recognition" label="Recognition Fund" icon={Award}
          value={fmt(summary?.recognition_fund_reserved)}
          sub={`${summary?.recognition_fund_count || 0} reserved entries`}
          color="violet-500" />
      </div>

      {/* KPI Row 2 */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
        <KPICard testId="kpi-partners" label="Active Partners" icon={Users}
          value={summary?.total_active_partners || 0}
          sub="Across all hierarchy levels" />
        <KPICard testId="kpi-events" label="Commission Events" icon={Zap}
          value={summary?.total_events || 0}
          sub="Subscription + wallet recharge events" />
        <KPICard testId="kpi-network" label="Network Health" icon={GitBranch}
          value={summary?.total_active_partners > 0 ? "Active" : "No Partners Yet"}
          sub="Build your partner network →" />
      </div>

      {/* Quick actions */}
      <div className="bg-card border border-border rounded-xl p-5">
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-3">Quick Actions</div>
        <div className="flex flex-wrap gap-2">
          {[
            { label: "Add Partner",       href: "/sm/hierarchy",          color: "bg-primary text-primary-foreground" },
            { label: "View Ledger",        href: "/sm/commission-ledger",  color: "border border-border text-foreground hover:bg-muted" },
            { label: "Configure Rates",    href: "/sm/commission-config",  color: "border border-border text-foreground hover:bg-muted" },
            { label: "Monthly Statements", href: "/sm/statements",         color: "border border-border text-foreground hover:bg-muted" },
            { label: "TDS Certificates",   href: "/sm/statements",         color: "border border-amber-500/30 text-amber-600 hover:bg-amber-500/5" },
          ].map(a => (
            <a key={a.label} href={a.href}
              className={`text-[12px] px-4 py-2 rounded-lg font-medium transition-all ${a.color}`}>
              {a.label}
            </a>
          ))}
        </div>
      </div>

      {/* Leaderboard */}
      <LeaderboardWidget />
    </div>
  );
}
